import {
  genYearAndMonthList,
  nthNextCalsByYearAndMonth,
  nthNextHlvsByYearAndMonth,
  nthNextMonthsByYearAndMonth,
  nthNextQtrsByYearAndMonth,
  nthNextSeasonsByYearAndMonth,
} from "@package/grid-utils";

/**
 * A custom time spread is expired when any leg is before today's period of
 * that type, which is the same period the market sheet starts from. The
 * current quarter, half, season, and calendar stay even though they began
 * in an earlier month. A leg we cannot parse is left alone.
 */
const PERIOD_KINDS = ["months", "quarters", "halves", "seasons", "cals"] as const;
type PeriodKind = (typeof PERIOD_KINDS)[number];

const MONTH_INDEX: Record<string, number> = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
};

type PeriodKey = { kind: PeriodKind; index: number };

type AdhocPeriodLike = {
  periodValue?: string | null;
};

type AdhocSpreadLike = {
  from?: AdhocPeriodLike | null;
  to?: AdhocPeriodLike | null;
  middle?: AdhocPeriodLike | null;
  middle2?: AdhocPeriodLike | null;
};

const compactPeriod = (period: string) => period.replace(/\s+/g, "").toUpperCase();

const fullYear = (yy: string) => 2000 + Number(yy);

function periodKey(periodValue: string): PeriodKey | null {
  const period = compactPeriod(periodValue);

  const month = /^(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(\d{2})$/.exec(period);
  if (month) {
    const monthIndex = MONTH_INDEX[month[1] ?? ""];
    if (monthIndex === undefined) return null;
    return { kind: "months", index: fullYear(month[2] ?? "") * 12 + monthIndex };
  }

  const quarter = /^Q([1-4])(\d{2})$/.exec(period);
  if (quarter) {
    return { kind: "quarters", index: fullYear(quarter[2] ?? "") * 4 + Number(quarter[1]) - 1 };
  }

  const half = /^H([12])(\d{2})$/.exec(period);
  if (half) {
    return { kind: "halves", index: fullYear(half[2] ?? "") * 2 + Number(half[1]) - 1 };
  }

  const season = /^(SUM|WIN)(\d{2})$/.exec(period);
  if (season) {
    return { kind: "seasons", index: fullYear(season[2] ?? "") * 2 + (season[1] === "WIN" ? 1 : 0) };
  }

  const cal = /^CAL(\d{2})$/.exec(period);
  if (cal) {
    return { kind: "cals", index: fullYear(cal[1] ?? "") };
  }

  return null;
}

/** Today's period for each row type, using the same labels the grid builds. */
function currentPeriodIndexes(now: Date): Partial<Record<PeriodKind, number>> {
  const anchor = genYearAndMonthList(1, now);
  const labels: Record<PeriodKind, string | undefined> = {
    months: nthNextMonthsByYearAndMonth(anchor)[0]?.[1],
    quarters: nthNextQtrsByYearAndMonth(anchor, true)[0]?.[1],
    halves: nthNextHlvsByYearAndMonth(anchor, true)[0]?.[1],
    seasons: nthNextSeasonsByYearAndMonth(anchor, true)[0]?.[1],
    cals: nthNextCalsByYearAndMonth(anchor, true)[0]?.[1],
  };

  const indexes: Partial<Record<PeriodKind, number>> = {};
  for (const kind of PERIOD_KINDS) {
    const label = labels[kind];
    if (!label) continue;
    const key = periodKey(label);
    if (key?.kind === kind) indexes[kind] = key.index;
  }
  return indexes;
}

function isPeriodBeforeCurrent(periodValue: string, current: Partial<Record<PeriodKind, number>>): boolean {
  const key = periodKey(periodValue);
  if (!key) return false;
  const currentIndex = current[key.kind];
  if (currentIndex === undefined) return false;
  return key.index < currentIndex;
}

const spreadLegs = (spread: AdhocSpreadLike) => [spread.from, spread.to, spread.middle, spread.middle2];

function hasExpiredLeg(spread: AdhocSpreadLike, current: Partial<Record<PeriodKind, number>>): boolean {
  return spreadLegs(spread).some((leg) => {
    const periodValue = leg?.periodValue;
    return typeof periodValue === "string" && periodValue.length > 0 && isPeriodBeforeCurrent(periodValue, current);
  });
}

export function isAdhocSpreadExpired(spread: AdhocSpreadLike, now: Date): boolean {
  return hasExpiredLeg(spread, currentPeriodIndexes(now));
}

export function withoutExpiredAdhocSpreads<T extends AdhocSpreadLike>(spreads: readonly T[], now: Date): T[] {
  const current = currentPeriodIndexes(now);
  return spreads.filter((spread) => !hasExpiredLeg(spread, current));
}
